import { rem } from 'polished';
import * as React from 'react';
import styled from 'styled-components';
import { ButtonIcon } from '../../_helpers/form/Button';
import {
  Chevron,
  OverlayArrow,
} from '../../../containers/AudienceIntro/_shared';

import Swiper from '../../_helpers/Swiper/Swiper';
import ImagePlaceholder from '../../_helpers/Image/ImagePlaceholder';
import { formatShop } from '../../../utilities/shops';
import { __ } from '@bart.sk-eshop/react-i18n';
import { Title } from '../Page/ContentContainer';
import { GoogleReview } from '../../GoogleReview/GoogleReview';
import { GoogleReviewOverall } from '../../GoogleReview/GoogleReviewOverall';

import loadable from '@loadable/component';

const StoreRow = styled.p`
  margin: 0 0 ${rem(0)};
  a {
    color: ${({ theme }) => theme.color.link};
    text-decoration: none;
  }
`;

const CustomTextWrapper = styled.div`
  margin-bottom: ${rem(15)};

  p {
    margin: 0px;
  }
`;

const BaseInfoWrapper = styled.div`
  display: flex;
`;

const BaseInfoColumn = styled.div`
  width: 100%;

  ${({ theme }) => theme.media(760)} {
    width: 50%;
  }
`;

const BaseInfoColumnRight = styled(BaseInfoColumn)`
  margin-right: ${rem(10)};
`;

const BaseInfoColumnLeft = styled(BaseInfoColumn)`
  margin-left: ${rem(10)};
  display: none;

  ${({ theme }) => theme.media(760)} {
    display: block;
  }
`;

const BaseInfoColumnContent = styled.div`
  margin-bottom: ${rem(10)};
`;

const LabelText = styled.div`
  font-size: ${rem(12)};
  color: #b3b3b3;
`;

const ContentText = styled.div`
  font-size: ${rem(18)};
`;

const TextButtonsWrapper = styled.div`
  display: flex;
  gap: ${rem(5)};
  flex-direction: column;
  justify-content: space-between;

  ${({ theme }) => theme.media(550)} {
    gap: ${rem(10)};
    flex-direction: row;
  }
`;

const ButtonsWrapper = styled.div`
  display: flex;
  gap: 10px;
  flex-direction: column;

  ${({ theme }) => theme.media(425)} {
    flex-direction: row;
  }
`;

const ButtonsWrapperContact = styled(ButtonsWrapper)`
  gap: ${rem(20)};
`;

const ContentButton = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 3px 12px;
  border: 2px solid #ccc;
  border-radius: 3px;
  background-color: white;
  cursor: pointer;
  text-decoration: none;
  font-size: ${rem(14)};
  color: black;
`;

const ContentTextWithIcon = styled.div`
  display: inline-flex;
  align-items: center;
  /* No background — the page behind is grey, a white box would show. */
  background-color: transparent;
  text-decoration: none;
  font-size: ${rem(16)};
  color: black;
  text-decoration: underline;
`;

const ContentButtonMobileWrapper = styled.div`
  display: block;
  ${({ theme }) => theme.media(760)} {
    display: none;
  }
`;

const ContentButtonIcon = styled(ButtonIcon)`
  ${({ theme }) => theme.size(18)};
  margin-right: ${rem(10)};
  transform: initial;
  /* The base IconWrapper svg carries its own fill (text colour) with equal
     specificity, so double the class to make the red reliably win. */
  && {
    fill: ${({ theme }) => theme.color.primary};
  }
`;

const ContentButtonIconContact = styled(ContentButtonIcon)`
  ${({ theme }) => theme.size(15)};
  margin-right: ${rem(7)};
`;

const MobileMapWrapper = styled.div`
  display: block;
  ${({ theme }) => theme.media(760)} {
    display: none;
  }
`;

const SliderArrow = styled(OverlayArrow)`
  ${({ theme }) => theme.media('md')} {
    top: 50%;
  }
  && {
    width: ${rem(48)};
    height: ${rem(48)};
    margin-top: 0;
    border: none;
    background: ${({ theme }) => theme.color.primary};
    box-shadow: 0 0 0 ${rem(3)} rgba(0, 0, 0, 0.1);
  }
  /* Focus keeps the base red — the button must not stay "lit" after a click. */
  &&:focus {
    background: ${({ theme }) => theme.color.primary};
  }
  &&:hover {
    background: #c5142f;
  }
  &&:before,
  &&:after {
    display: none;
  }
  &&.swiper-button-disabled {
    display: none;
  }
`;

const Image = styled(ImagePlaceholder)`
  display: block;
  padding: 0;
  object-fit: cover;
  width: 100%;
  height: 100%;
`;

const GallerySwiper = styled(Swiper)`
  margin-bottom: ${rem(20)};

  .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
  }
`;

const ReviewsSwiper = styled(Swiper)`
  .swiper-slide {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: auto;
  }
`;

const GallerySwipperWrapper = styled.div``;

const GalleryPagination = styled.div`
  text-align: center;
  margin-top: ${rem(10)};

  .swiper-pagination-bullet {
    background: #0e0e0e;
    /* The global 8px bullet gap is scoped to bullets INSIDE the swiper
       container, so it must be restated for this external element. */
    margin: 0 ${rem(8)};
  }

  .swiper-pagination-bullet-active {
    background: black;
  }
`;

const ReviewsPagination = styled(GalleryPagination)`
  .swiper-pagination-bullet {
    background: #808080;
    opacity: 0.35;
  }

  .swiper-pagination-bullet-active {
    background: #808080;
    opacity: 1;
  }
`;

const GoogleReviewsWrapper = styled.div`
  margin-bottom: ${rem(20)};
`;

const GoogleReviewOverallWrapper = styled.div`
  margin-bottom: ${rem(20)};
  margin-top: ${rem(20)};
`;

const GalleryModal = loadable(
  () => import('../../_helpers/Modal/GalleryModal'),
);

interface StoreDetailBaseProps {
  detail: any;
}

interface StoreDetailBaseState {
  mobileMapVisible: boolean;
  modalVisible: boolean;
  activeIndex: number | null;
}

export class StoreDetailBase extends React.PureComponent<
  StoreDetailBaseProps,
  StoreDetailBaseState
> {
  constructor(props) {
    super(props);
    this.state = {
      mobileMapVisible: false,
      modalVisible: false,
      activeIndex: null,
    };
  }

  public onShowMapClick = () => {
    this.setState({ mobileMapVisible: true });
  };

  public onThumbClick = (index) => {
    const isPC = (window && window.innerWidth) > 425;
    if (isPC) {
      this.setState({ modalVisible: true, activeIndex: index });
    }
  };

  public closeModal = () => {
    this.setState({ modalVisible: false });
  };

  public render() {
    let { detail } = this.props;

    const renderPrevButton = () => (
      <SliderArrow
        type="button"
        className="swiper-button-prev left"
        aria-label={__('Predošlé')}
      >
        <Chevron dir="left" />
      </SliderArrow>
    );
    const renderNextButton = () => (
      <SliderArrow
        type="button"
        className="swiper-button-next right"
        aria-label={__('Ďalšie')}
      >
        <Chevron dir="right" />
      </SliderArrow>
    );

    const swiperParams = {
      slidesPerView: 2,
      spaceBetween: 8,
      pagination: {
        el: '.gallery-pagination',
      },
      renderPagination: () => null,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      renderPrevButton,
      renderNextButton,
      breakpoints: {
        0: {
          slidesPerView: 2,
        },
        790: {
          slidesPerView: 2,
        },
      },
    };

    const reviewSwiperParams = {
      slidesPerView: 4,
      spaceBetween: 8,
      pagination: {
        el: '.reviews-pagination',
      },
      renderPagination: () => null,
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      },
      renderPrevButton,
      renderNextButton,
      breakpoints: {
        0: {
          slidesPerView: 2,
        },
        790: {
          slidesPerView: 4,
        },
      },
    };

    detail = formatShop(detail);

    let images = [];
    if (detail && detail.content && detail.content.photogallery) {
      images = detail.content.photogallery.map((item, i) => {
        return item.document_path;
      });
    }
    images.shift();

    return (
      <>
        <Title> {detail.name} </Title>
        <BaseInfoWrapper>
          <BaseInfoColumnRight>
            <BaseInfoColumnContent>
              <LabelText>Adresa:</LabelText>
              <TextButtonsWrapper>
                <ContentText>
                  {' '}
                  {detail.address}
                  <br />
                  {detail.city} {detail.zip}{' '}
                </ContentText>
                <ButtonsWrapper>
                  {detail.iframe_map ? (
                    <ContentButtonMobileWrapper>
                      {' '}
                      <ContentButton onClick={() => this.onShowMapClick()}>
                        {' '}
                        <ContentButtonIcon icon="location2" /> Zobraziť na mape{' '}
                      </ContentButton>
                    </ContentButtonMobileWrapper>
                  ) : (
                    <></>
                  )}
                  {detail.navigate_link ? (
                    <a href={detail.navigate_link}>
                      {' '}
                      <ContentButton>
                        {' '}
                        <ContentButtonIcon icon="navigation" /> Naviguj{' '}
                      </ContentButton>
                    </a>
                  ) : (
                    <></>
                  )}
                </ButtonsWrapper>
              </TextButtonsWrapper>
            </BaseInfoColumnContent>

            {this.state.mobileMapVisible ? (
              <MobileMapWrapper>
                <div
                  dangerouslySetInnerHTML={{
                    __html: detail.iframe_map,
                  }}
                />
              </MobileMapWrapper>
            ) : (
              <></>
            )}

            {detail.opening_hours_formatted ? (
              <BaseInfoColumnContent>
                <LabelText>Otváracia doba:</LabelText>
                <ContentText>
                  {detail.opening_hours_formatted.map((oh, i) => {
                    return <StoreRow key={i}>{oh}</StoreRow>;
                  })}
                </ContentText>

                {typeof detail.content?.json_content
                  ?.additional_opening_hours === 'string' ? (
                  <CustomTextWrapper
                    dangerouslySetInnerHTML={{
                      __html:
                        detail.content?.json_content?.additional_opening_hours,
                    }}
                  />
                ) : (
                  ''
                )}
              </BaseInfoColumnContent>
            ) : (
              ''
            )}

            <BaseInfoColumnContent>
              <LabelText style={{ marginBottom: 5 }}>Kontakt:</LabelText>
              <ButtonsWrapperContact>
                <a href={`tel:${detail.phone}`}>
                  <ContentTextWithIcon>
                    {' '}
                    <ContentButtonIconContact icon="phone" /> {detail.phone}{' '}
                  </ContentTextWithIcon>
                </a>
                <a href={`mailto:${detail.email}`}>
                  <ContentTextWithIcon>
                    {' '}
                    <ContentButtonIconContact icon="mail" /> {detail.email}{' '}
                  </ContentTextWithIcon>
                </a>
              </ButtonsWrapperContact>
            </BaseInfoColumnContent>
          </BaseInfoColumnRight>
          <BaseInfoColumnLeft>
            <div
              dangerouslySetInnerHTML={{
                __html: detail.iframe_map,
              }}
            />
          </BaseInfoColumnLeft>
        </BaseInfoWrapper>

        {typeof detail.content?.json_content?.body === 'string' ? (
          <div>
            <LabelText>{__('Dalšie informácie o predajni')}:</LabelText>
            <CustomTextWrapper
              dangerouslySetInnerHTML={{
                __html: detail.content?.json_content?.body,
              }}
            />
          </div>
        ) : (
          <></>
        )}

        <div>
          <LabelText style={{ marginBottom: 5 }}>
            {__('Fotogaléria')}:
          </LabelText>
          <GallerySwipperWrapper>
            <GallerySwiper params={swiperParams}>
              {images.map((image, index) => {
                return (
                  <div key={index}>
                    <Image
                      src={image}
                      h={350}
                      w={600}
                      resizeType={'cover'}
                      onClick={() => this.onThumbClick(index)}
                    />
                  </div>
                );
              })}
            </GallerySwiper>
          </GallerySwipperWrapper>
          <GalleryPagination className="gallery-pagination" />
        </div>

        {detail?.google_review_rating && detail?.google_review_rating_count ? (
          <GoogleReviewOverallWrapper>
            <GoogleReviewOverall
              reviewOverallData={{
                google_review_rating: detail.google_review_rating,
                google_review_rating_count: detail.google_review_rating_count,
              }}
              googlePlaceId={detail.google_place_id}
            />
          </GoogleReviewOverallWrapper>
        ) : (
          <></>
        )}

        {detail?.google_reviews?.length ? (
          <GoogleReviewsWrapper>
            <ReviewsSwiper params={reviewSwiperParams}>
              {detail.google_reviews.map((googleReview, i) => {
                return (
                  <div key={i}>
                    <GoogleReview reviewData={googleReview} />
                  </div>
                );
              })}
            </ReviewsSwiper>
            <ReviewsPagination className="reviews-pagination" />
          </GoogleReviewsWrapper>
        ) : (
          <></>
        )}

        {this.state.modalVisible && (
          <GalleryModal
            pictures={images}
            activeIndex={this.state.activeIndex}
            onCloseButtonClick={this.closeModal}
          />
        )}
      </>
    );
  }
}
